import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { useMemo } from "react";
import { updateSourceQueryCacheWithWords } from "../domain_data";
import { useCurrentDevice } from "../hooks";
import {
  SourceLocation,
  WordView,
  useSetMultipleAsGotIt,
  useSetMultipleAsIgnored,
} from "../api-generated";
import Tiles from "./Tiles";

interface Props {
  wordsSelected: WordView[];
  onClickRemove: (wordId: number) => void;
  onCloseMenu: () => void;
  location: SourceLocation;
}

const MultiSelectMenu: React.FC<Props> = ({
  wordsSelected,
  onClickRemove,
  onCloseMenu,
  location,
}) => {
  const qc = useQueryClient();
  const ignoreAllMut = useSetMultipleAsIgnored();
  const gotItAllMut = useSetMultipleAsGotIt();
  const { isPhone } = useCurrentDevice();

  const mutateOptions = {
    onSuccess: (words: WordView[]) => {
      updateSourceQueryCacheWithWords(words, qc, location);
      onCloseMenu();
    },
  };

  const wordIds = useMemo(
    () => wordsSelected.map((w) => w.id),
    [JSON.stringify(wordsSelected)]
  );

  const ignoreAll = () => {
    ignoreAllMut.mutate({ data: wordIds }, mutateOptions);
  };

  const gotItAll = () => {
    gotItAllMut.mutate({ data: wordIds }, mutateOptions);
  };

  const isFormDisabled = wordsSelected.length === 0;

  return (
    <div className="w-full h-full px-4">
      <div className="py-2 flex justify-between">
        <b>Multi-Select</b>
        <button onClick={onCloseMenu}>
          <b>X</b>
        </button>
      </div>
      <div className="flex justify-between gap-4 h-full">
        {isPhone ? (
          <div className="w-1/2 text-center">
            {wordsSelected.length}{" "}
            {wordsSelected.length === 1 ? "word" : "words"} selected
          </div>
        ) : (
          <div className="overflow-x-auto w-3/4">
            <Tiles
              elements={wordsSelected.map((word) => ({
                key: String(word.id),
                text: word.displayString,
              }))}
              emptyMessage="No words selected"
              onClickRemove={(toRemoveWordId: string) =>
                onClickRemove(Number(toRemoveWordId))
              }
            />
          </div>
        )}

        <div className={clsx({ "w-1/2": isPhone, "w-1/4": !isPhone })}>
          <div className="h-12 flex flex-row gap-2">
            <div className="w-1/2 border border-gray-400 bg-gray-50 rounded h-full text-center flex flex-col justify-center">
              <button onClick={ignoreAll} disabled={isFormDisabled}>
                <div className="text-center w-full">Ignore</div>
              </button>
            </div>

            <div className="w-1/2 border border-gray-400 bg-gray-50 rounded h-full text-center flex flex-col justify-center">
              <button onClick={gotItAll} disabled={isFormDisabled}>
                <div className="text-center w-full">Got it</div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultiSelectMenu;
